import i18n from 'i18next'

import { newDateFormatted } from 'app/utils/date'

/** Delay (in milliseconds) to debounce when typing a text search. */
export const DELAY_DEBOUNCE = 800

/** The maximum number of lines to display a vat report detail. */
export const MAX_LINES_REPORT_DETAIL = 10_000

/** Maximum interval range date between two dates. */
export const MAX_DAY_RANGE = 62

/** The default date to start the filter. */
export const DEFAULT_DATE_START = newDateFormatted(-7)

/** The default date to end the filter. */
export const DEFAULT_DATE_END = newDateFormatted()

/** Size maximum (200 Ko) of the image. */
export const MAX_IMAGE_SIZE = 1_024 * 200

/** Size limit to load more data. */
export const PAGINATION_BOTTOM = 150

/** Width of the product images. */
export const PRODUCT_IMAGE_WIDTH = 200

/** Height of the product images. */
export const PRODUCT_IMAGE_HEIGHT = 200

/** Api endpoint used with geocode. */
export const API_GEOCODE = 'https://nominatim.openstreetmap.org/search'

/** MIME types of files. */
export const mimeTypes = {
    AVIF: 'image/avif',
    CSV: 'text/csv',
    EXCEL: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    JPEG: 'image/jpeg',
    PDF: 'application/pdf',
    PNG: 'image/png',
    SVG: 'image/svg',
    WEBP: 'image/webp',
}

export const links = {
    KONVIVES: 'https://konvives.com',
    TICKETING: 'https://e-thik.odoo.com',
}

/** Make a pause with the time given (in milliseconds). */
export const pause = async (time: number) => {
    await new Promise((resolve) => {
        setTimeout(resolve, time)
    })
}

/** Know if the value passed is a number or not. */
export const isNumeric = (numberToTest: string | number) =>
    (typeof numberToTest === 'number' ||
        (typeof numberToTest === 'string' && numberToTest.trim() !== '')) &&
    !Number.isNaN(Number.parseFloat(numberToTest.toString()))

/** Transform the query into an array of number. */
export const queryIntoArrayNumber = (query: string) =>
    query
        .split(',')
        .map(Number)
        .filter((value) => !Number.isNaN(value))

/** Get the number formatted depending on the locale and the options. */
export const getNumberFormatted = (
    number: number | string,
    options?: Intl.NumberFormatOptions,
) => {
    const realNumber =
        typeof number === 'string' ? Number.parseFloat(number) : number

    return new Intl.NumberFormat(i18n.language, options).format(realNumber)
}

/** Get the price formatted. */
export const getPriceFormatted = (price: number | string) => {
    return getNumberFormatted(price, {
        style: 'currency',
        currency: 'EUR',
    })
}

/** Get the price formatted. */
export const getPriceWeightFormatted = (price: number | string) => {
    const priceFormatted = getNumberFormatted(price, {
        style: 'currency',
        currency: 'EUR',
    })

    const positionEuro = priceFormatted.indexOf('€') + 1

    return `${priceFormatted.slice(0, positionEuro)} / Kg ${priceFormatted.slice(positionEuro)}`
}

/** Get the percentage formatted. */
export const getPercentageFormatted = (percentage: number) => {
    return getNumberFormatted(percentage / 100, {
        style: 'percent',
        maximumFractionDigits: 1,
    })
}

/** Get the byte formatted. */
export const getByteFormatted = (size: number) => {
    return getNumberFormatted((size / 1_024) * 1_000, {
        style: 'unit',
        unit: 'byte',
        unitDisplay: 'narrow',
        notation: 'compact',
    })
}

/** Get the percentage formatted. */
export const getDayFormatted = (value: number) => {
    return getNumberFormatted(value, {
        style: 'unit',
        unit: 'day',
    })
}

/** Get the weight formatted. */
export const getWeightFormatted = (weight: number | string): string => {
    return getNumberFormatted(weight, {
        style: 'unit',
        unit: 'gram',
        notation: 'compact',
        unitDisplay: 'narrow',
    })
}

export const parsePrice = (value: string): number =>
    Number.parseFloat(Number.parseFloat(value).toFixed(2))
